import { createMachine } from '@monofront/xstate-v4';
import { SELECT_ADDRESS_TRANSITIONS } from './transitions';
import { SELECT_ADDRESS_STATES } from './states';
import {
  ISelectAddressMachineContext,
  ISelectAddressMachineEvents,
  ISelectAddressServices,
} from './types';
import {
  isAddressesEmpty,
  isAddressNotSelected,
  isAddressPrefilled,
  isAddressSelected,
  isUserReady,
} from './guards';
import {
  assignAddressUpdated,
  assignCreatedAddress,
  assignCreateForBillingOnly,
  SELECT_ADDRESS_EXTERNAL_ACTIONS,
  selectBillingEqualShippingAddress,
  selectCreatedAddress,
  sendToShippingSelectedToParent,
  selectBillingAddressTransition,
  selectShippingAddressTransition,
  assignAddressListReceived,
  assignModalOrFirstAddressOnTheList,
  selectUpdatedAddress,
  assignDeliveryInfoToRelatedAddress,
  assignUserReady,
} from './actions';
import { SELECT_ADDRESS_EXTERNAL_SERVICES } from './services';
import { CHECKOUT_TAGS_ADDRESS_FORM } from '../tags';
import { CHECKOUT_TRANSITIONS } from '../transitions';

export const SELECT_ADDRESS_MACHINE_ID = 'select-address';

export const selectAddressMachine = createMachine<
  ISelectAddressMachineContext,
  ISelectAddressMachineEvents
>({
  predictableActionArguments: true,
  id: SELECT_ADDRESS_MACHINE_ID,
  schema: {
    context: {} as ISelectAddressMachineContext,
    events: {} as ISelectAddressMachineEvents,
    services: {} as ISelectAddressServices,
  },
  initial: SELECT_ADDRESS_STATES.INITIAL,
  context: {
    isUserReady: false,
    addresses: [],
    shippingAddressSelected: null,
    billingAddressSelected: null,
    creatingForBillingOnly: false,
    addressModalRef: null,
    isBillingAndShippingEqual: true,
    addressPrefilled: null,
    isAddressEmpty: false,
  },
  states: {
    [SELECT_ADDRESS_STATES.INITIAL]: {
      entry: 'spawnMachines',
      on: {
        [CHECKOUT_TRANSITIONS.USER_READY]: {
          target: SELECT_ADDRESS_STATES.WAIT_CHECKOUT_STARTING,
          actions: [assignUserReady],
        },
        [CHECKOUT_TRANSITIONS.START_CHECKOUT]: [
          {
            target: SELECT_ADDRESS_STATES.GET,
            cond: isUserReady,
          },
          { target: SELECT_ADDRESS_STATES.WAIT_USER_READY },
        ],
      },
    },
    [SELECT_ADDRESS_STATES.WAIT_CHECKOUT_STARTING]: {
      on: {
        [CHECKOUT_TRANSITIONS.START_CHECKOUT]: {
          target: SELECT_ADDRESS_STATES.GET,
        },
      },
    },
    [SELECT_ADDRESS_STATES.WAIT_USER_READY]: {
      tags: [CHECKOUT_TAGS_ADDRESS_FORM],
      on: {
        [CHECKOUT_TRANSITIONS.USER_READY]: {
          target: SELECT_ADDRESS_STATES.GET_WITH_FORM,
          actions: [assignUserReady],
        },
      },
    },
    [SELECT_ADDRESS_STATES.GET_WITH_FORM]: {
      tags: [CHECKOUT_TAGS_ADDRESS_FORM],
      initial: SELECT_ADDRESS_STATES.ADDRESSES_FETCHING,
      states: {
        [SELECT_ADDRESS_STATES.ADDRESSES_FETCHING]: {
          invoke: {
            src: SELECT_ADDRESS_EXTERNAL_SERVICES.getAddresses,
            onDone: [
              {
                target: SELECT_ADDRESS_STATES.GET_SUCCESS,
                actions: [assignAddressListReceived],
              },
            ],
            onError: {
              target: SELECT_ADDRESS_STATES.GET_ERROR,
            },
          },
        },
        [SELECT_ADDRESS_STATES.GET_ERROR]: {},
        [SELECT_ADDRESS_STATES.GET_SUCCESS]: {
          always: [
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.ADDRESS_PREFILLED}`,
              cond: isAddressPrefilled,
            },
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.NO_ADDRESS_SAVED}`,
              cond: isAddressesEmpty,
            },
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.ADDRESS_SELECTED}`,
              cond: isAddressNotSelected,
              actions: [
                assignModalOrFirstAddressOnTheList,
                sendToShippingSelectedToParent,
              ],
            },
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.ADDRESS_SELECTED}`,
              cond: isAddressSelected,
            },
          ],
        },
      },
      on: {
        [SELECT_ADDRESS_TRANSITIONS.CREATE_ADDRESS]:
          SELECT_ADDRESS_STATES.ADDRESS_CREATING,
      },
    },
    [SELECT_ADDRESS_STATES.GET]: {
      initial: SELECT_ADDRESS_STATES.ADDRESSES_FETCHING,
      states: {
        [SELECT_ADDRESS_STATES.ADDRESSES_FETCHING]: {
          invoke: {
            src: SELECT_ADDRESS_EXTERNAL_SERVICES.getAddresses,
            onDone: [
              {
                target: SELECT_ADDRESS_STATES.GET_SUCCESS,
                actions: [assignAddressListReceived],
              },
            ],
            onError: {
              target: SELECT_ADDRESS_STATES.GET_ERROR,
            },
          },
        },
        [SELECT_ADDRESS_STATES.GET_ERROR]: {},
        [SELECT_ADDRESS_STATES.GET_SUCCESS]: {
          always: [
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.ADDRESS_PREFILLED}`,
              cond: isAddressPrefilled,
            },
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.NO_ADDRESS_SAVED}`,
              cond: isAddressesEmpty,
            },
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.ADDRESS_SELECTED}`,
              cond: isAddressNotSelected,
              actions: [
                assignModalOrFirstAddressOnTheList,
                sendToShippingSelectedToParent,
              ],
            },
            {
              target: `#${SELECT_ADDRESS_MACHINE_ID}.${SELECT_ADDRESS_STATES.ADDRESS_SELECTED}`,
              cond: isAddressSelected,
              actions: [sendToShippingSelectedToParent],
            },
          ],
        },
      },
    },
    [SELECT_ADDRESS_STATES.ADDRESS_PREFILLED]: {
      tags: [CHECKOUT_TAGS_ADDRESS_FORM],
      on: {
        [SELECT_ADDRESS_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_EQUAL_SHIPPING_ADDRESS]: {
          actions: selectBillingEqualShippingAddress,
        },
        [SELECT_ADDRESS_TRANSITIONS.CREATE_ADDRESS]:
          SELECT_ADDRESS_STATES.ADDRESS_CREATING,
        [SELECT_ADDRESS_TRANSITIONS.UPDATE_ADDRESS]:
          SELECT_ADDRESS_STATES.ADDRESS_UPDATING,
      },
    },
    [SELECT_ADDRESS_STATES.NO_ADDRESS_SAVED]: {
      tags: [CHECKOUT_TAGS_ADDRESS_FORM],
      on: {
        [SELECT_ADDRESS_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_EQUAL_SHIPPING_ADDRESS]: {
          actions: selectBillingEqualShippingAddress,
        },
        [SELECT_ADDRESS_TRANSITIONS.CREATE_ADDRESS]: {
          target: SELECT_ADDRESS_STATES.ADDRESS_CREATING,
        },
        [SELECT_ADDRESS_TRANSITIONS.UPDATE_ADDRESS]:
          SELECT_ADDRESS_STATES.ADDRESS_UPDATING,
      },
    },
    [SELECT_ADDRESS_STATES.ADDRESS_SELECTED]: {
      on: {
        [SELECT_ADDRESS_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_EQUAL_SHIPPING_ADDRESS]: {
          actions: selectBillingEqualShippingAddress,
        },
        [SELECT_ADDRESS_TRANSITIONS.CREATE_ADDRESS]:
          SELECT_ADDRESS_STATES.ADDRESS_CREATING,
        [SELECT_ADDRESS_TRANSITIONS.UPDATE_ADDRESS]:
          SELECT_ADDRESS_STATES.ADDRESS_UPDATING,
        [SELECT_ADDRESS_TRANSITIONS.SET_DELIVERY_INFOS]: {
          actions: assignDeliveryInfoToRelatedAddress,
        },
      },
    },

    [SELECT_ADDRESS_STATES.ADDRESS_CREATING]: {
      entry: [assignCreateForBillingOnly],
      invoke: {
        src: SELECT_ADDRESS_EXTERNAL_SERVICES.createAddress,
        onDone: [
          {
            target: SELECT_ADDRESS_STATES.ADDRESS_SELECTED,
            actions: [assignCreatedAddress, selectCreatedAddress],
          },
        ],
        onError: [
          {
            target: SELECT_ADDRESS_STATES.ADDRESS_SELECTED,
            actions: [SELECT_ADDRESS_EXTERNAL_ACTIONS.createAddressError],
          },
        ],
      },
    },
    [SELECT_ADDRESS_STATES.ADDRESS_UPDATING]: {
      entry: [assignCreateForBillingOnly],
      invoke: {
        src: SELECT_ADDRESS_EXTERNAL_SERVICES.updateAddress,
        onDone: [
          {
            target: SELECT_ADDRESS_STATES.ADDRESS_SELECTED,
            actions: [assignAddressUpdated, selectUpdatedAddress],
          },
        ],
        onError: [
          {
            target: SELECT_ADDRESS_STATES.ADDRESS_SELECTED,
            actions: [SELECT_ADDRESS_EXTERNAL_ACTIONS.updateAddressError],
          },
        ],
      },
    },
  },
});
